import { AlertTriangle, ChevronDown, ChevronUp, DollarSign, FileText, Info, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import DataQualityBadge from '../../components/analytics/DataQualityBadge';
import EmptyStateGuide from '../../components/analytics/EmptyStateGuide';
import MetricDefinitionsDrawer from '../../components/analytics/MetricDefinitionsDrawer';
import MetricTooltip from '../../components/analytics/MetricTooltip';
import PageGuide from '../../components/analytics/PageGuide';
import { analyticsService } from '../../services/api';
import '../Analytics.css';
import AnalyticsLayout from './AnalyticsLayout';

function getDefaultFromDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
}

const formatCurrency = (value) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);

const formatNumber = (value) =>
    new Intl.NumberFormat('it-IT').format(value || 0);

const formatPct = (value) =>
    value != null ? value.toFixed(2) + '%' : '—';

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT');
};

const INVOICE_STATUS_LABEL = {
    DRAFT: 'Bozza',
    SENT: 'Inviata',
    WAITING: 'In attesa SDI',
    ACCEPTED: 'Accettata',
    DELIVERED: 'Consegnata',
    REJECTED: 'Rifiutata',
    FAILED: 'Errore',
};

const INVOICE_STATUS_COLOR = {
    DRAFT: '#94a3b8',
    SENT: '#3b82f6',
    WAITING: '#f59e0b',
    ACCEPTED: '#10b981',
    DELIVERED: '#6366f1',
    REJECTED: '#ef4444',
    FAILED: '#dc2626',
};

// anomaly thresholds
const INVOICE_ANOMALY_RATIO = 1.1;   // invoices > estimatedFee * 1.1
const TAKE_RATE_MAX = 0.30;           // > 30%
const TAKE_RATE_MIN = 0.005;          // < 0.5%
const LOW_SAMPLE_DAYS = 7;
const LOW_SAMPLE_TXN = 10;
const VENUE_TABLE_LIMIT = 10;

export default function AnalyticsRevenue() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        from: getDefaultFromDate(),
        to: new Date().toISOString().split('T')[0]
    });
    const [showAllVenues, setShowAllVenues] = useState(false);
    const [showAllInvoices, setShowAllInvoices] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await analyticsService.getRevenue({ from: filters.from, to: filters.to });
            setData(result);
        } catch (err) {
            console.error('Failed to load revenue data:', err);
            setError(err.response?.data?.message || err.message || 'Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // derived values — computed once data is available
    const estimatedTakeRate = data && data.grossVolume > 0
        ? data.estimatedSoontipFee / data.grossVolume
        : null;
    const invoiceToGtvRatio = data && data.grossVolume > 0
        ? data.confirmedRevenue.amount / data.grossVolume
        : null;
    const showInvoiceAnomalyWarning = data
        && data.estimatedSoontipFee > 0
        && data.confirmedRevenue.amount > data.estimatedSoontipFee * INVOICE_ANOMALY_RATIO;
    const showTakeRateWarning = estimatedTakeRate != null
        && (estimatedTakeRate > TAKE_RATE_MAX || (estimatedTakeRate < TAKE_RATE_MIN && data.grossVolume > 0));
    const periodDays = data?.periodDays || 30;
    const showRunRateCaveat = periodDays < LOW_SAMPLE_DAYS || (data?.transactionCount || 0) < LOW_SAMPLE_TXN;
    const visibleVenues = showAllVenues
        ? (data?.revenueByVenue || [])
        : (data?.revenueByVenue || []).slice(0, VENUE_TABLE_LIMIT);
    const visibleInvoices = showAllInvoices
        ? (data?.invoiceList || [])
        : (data?.invoiceList || []).slice(0, 10);

    return (
        <AnalyticsLayout filters={filters} onFiltersChange={setFilters}>
            <PageGuide
                title="Revenue Soontip"
                summary="GTV (volume mance), fee stimata e fatture emesse nel periodo. Attenzione: GTV ≠ revenue Soontip. Le fatture sono mostrate per data emissione, non per competenza transazionale."
            />

            {loading && !data && (
                <div className="analytics-loading">
                    <div className="spinner"></div>
                    <p>Caricamento dati revenue...</p>
                </div>
            )}

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    <strong>Errore:</strong> {error}
                </div>
            )}

            {data && (
                <>
                    {/* Toolbar */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            Periodo: {data.period?.from} → {data.period?.to}
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <MetricDefinitionsDrawer metricKeys={['totalTipVolume', 'estimatedSoontipFee', 'invoicedSoontipFee', 'pendingRevenue']} />
                            <button className="btn-refresh" onClick={loadData} disabled={loading} style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
                                {loading ? '⏳' : '🔄'} Aggiorna
                            </button>
                        </div>
                    </div>

                    {/* GTV ≠ Revenue banner */}
                    <div className="revenue-gtv-warning">
                        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                        <div>
                            <strong>⚠ GTV ≠ Revenue Soontip.</strong>{' '}
                            Il GTV è il totale delle mance processate dai clienti ai percettori.
                            La revenue Soontip è rappresentata dalla fee stimata sulle transazioni o dalle fee effettivamente fatturate.
                            Non usare il GTV come proxy della revenue nei report formali.
                        </div>
                    </div>

                    {/* Anomaly: invoices > estimated fee */}
                    {showInvoiceAnomalyWarning && (
                        <div className="revenue-anomaly-warning">
                            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
                            <div>
                                <strong>Anomalia:</strong> Le fatture emesse nel periodo ({formatCurrency(data.confirmedRevenue.amount)})
                                superano la fee stimata sulle transazioni del periodo ({formatCurrency(data.estimatedSoontipFee)}).
                                Verificare se le fatture si riferiscono a periodi precedenti o includono altre componenti di ricavo.
                            </div>
                        </div>
                    )}

                    {/* Anomaly: take rate out of range */}
                    {showTakeRateWarning && (
                        <div className="revenue-anomaly-warning">
                            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
                            <div>
                                <strong>Take rate anomalo:</strong> Il take rate stimato ({formatPct((estimatedTakeRate || 0) * 100)}) è fuori dall'intervallo atteso (0.5%–30%).
                                Verificare la configurazione fee sulle transazioni del periodo.
                            </div>
                        </div>
                    )}

                    {/* ── SECTION: Volume Transato (GTV) ── */}
                    <div className="kpi-section">
                        <h2 className="section-title">Volume Transato (GTV)</h2>
                        <div className="kpi-grid">
                            <MetricTooltip metricKey="totalTipVolume">
                                <div className="kpi-card kpi-accent">
                                    <div className="kpi-icon"><DollarSign size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Gross Tip Volume</div>
                                        <div className="kpi-value">{formatCurrency(data.grossVolume)}</div>
                                        <div className="kpi-meta">
                                            <TrendingUp size={14} />
                                            <span>{formatNumber(data.transactionCount)} transazioni</span>
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><TrendingUp size={28} /></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">
                                        Run-rate Mensile GTV
                                        <span style={{ marginLeft: '6px', cursor: 'help' }} title="Proiezione a 30 giorni basata sulla media giornaliera del periodo selezionato. Formula: (GTV / giorni periodo) × 30">
                                            <Info size={12} color="#94a3b8" />
                                        </span>
                                    </div>
                                    <div className="kpi-value">{formatCurrency(data.monthlyRunRate?.grossVolume)}</div>
                                    <div className="kpi-meta"><span>Media giornaliera × 30 giorni</span></div>
                                    {showRunRateCaveat && (
                                        <div className="kpi-low-volume">
                                            ⚠ Campione ridotto — dato poco significativo
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── SECTION: Fee Stimata Soontip ── */}
                    <div className="kpi-section">
                        <h2 className="section-title">Fee Stimata Soontip</h2>
                        <div className="kpi-grid">
                            <MetricTooltip metricKey="estimatedSoontipFee">
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon"><span style={{ fontSize: '22px', fontWeight: 700 }}>%</span></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">
                                            Fee Stimata (periodo)
                                            <span style={{ marginLeft: '6px' }}>
                                                <DataQualityBadge
                                                    status={data.feeDataCoverage === 100 ? 'complete' : data.feeDataCoverage > 0 ? 'partial' : 'unavailable'}
                                                    note={data.feeDataCoverage < 100 ? `${data.feeDataCoverage}% copertura snapshot` : null}
                                                />
                                            </span>
                                        </div>
                                        <div className="kpi-value">{formatCurrency(data.estimatedSoontipFee)}</div>
                                        {/* Expanded data quality details */}
                                        <div className="revenue-fee-coverage">
                                            <div className="revenue-fee-coverage-row">
                                                <span>Transazioni con snapshot fee:</span>
                                                <strong>{formatNumber(data.feeDetails?.txWithFeeData)} / {formatNumber(data.feeDetails?.totalTx)}</strong>
                                            </div>
                                            {data.feeDetails?.txWithoutFeeData > 0 && (
                                                <div className="revenue-fee-coverage-row revenue-fee-coverage-row--warn">
                                                    <span>Senza snapshot (escluse dal calcolo):</span>
                                                    <strong>{formatNumber(data.feeDetails.txWithoutFeeData)}</strong>
                                                </div>
                                            )}
                                            <div className="revenue-fee-coverage-row">
                                                <span>GTV coperto da snapshot:</span>
                                                <strong>{formatCurrency(data.feeDetails?.coveredGTV)}</strong>
                                            </div>
                                            {data.feeDetails?.uncoveredGTV > 0 && (
                                                <div className="revenue-fee-coverage-row revenue-fee-coverage-row--warn">
                                                    <span>GTV senza snapshot:</span>
                                                    <strong>{formatCurrency(data.feeDetails.uncoveredGTV)}</strong>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <MetricTooltip metricKey="pendingRevenue">
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon"><span style={{ fontSize: '22px' }}>⏳</span></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Revenue Pending (non fatturata)</div>
                                        <div className="kpi-value">{formatCurrency(data.pendingRevenue)}</div>
                                        <div className="kpi-meta"><span>Fee stimata non ancora fatturata nel periodo</span></div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            <div className="kpi-card kpi-secondary">
                                <div className="kpi-icon"><TrendingUp size={28} /></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">
                                        Run-rate Fee Mensile
                                        <span style={{ marginLeft: '6px', cursor: 'help' }} title="Proiezione a 30 giorni basata sulla media giornaliera della fee stimata nel periodo. Formula: (fee stimata / giorni periodo) × 30">
                                            <Info size={12} color="#94a3b8" />
                                        </span>
                                    </div>
                                    <div className="kpi-value">{formatCurrency(data.monthlyRunRate?.estimatedFee)}</div>
                                    <div className="kpi-meta"><span>Media giornaliera × 30 giorni</span></div>
                                    {showRunRateCaveat && (
                                        <div className="kpi-low-volume">
                                            ⚠ Campione ridotto — dato poco significativo
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── SECTION: Fatture emesse nel periodo ── */}
                    <div className="kpi-section">
                        <h2 className="section-title">Fatture emesse nel periodo</h2>
                        <div className="revenue-invoice-caveat">
                            <Info size={13} style={{ flexShrink: 0, marginTop: '2px' }} />
                            <span>
                                Importo fatturato con data emissione nel periodo selezionato.
                                Può riferirsi a competenze di periodi precedenti.
                                Non usare come "Revenue confermata" senza riconciliazione esplicita con le transazioni del periodo.
                            </span>
                        </div>
                        <div className="kpi-grid">
                            <MetricTooltip metricKey="invoicedSoontipFee">
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon"><FileText size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Fatture emesse nel periodo</div>
                                        <div className="kpi-value">{formatCurrency(data.confirmedRevenue?.amount)}</div>
                                        <div className="kpi-meta">
                                            <span>{data.confirmedRevenue?.invoiceCount || 0} fatture (Accepted / Delivered / Sent)</span>
                                        </div>
                                        <div className="kpi-meta" style={{ marginTop: '4px', fontSize: '0.72rem', color: '#94a3b8' }}>
                                            <span>Filtrate per data emissione — periodo competenza non disponibile</span>
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>
                        </div>
                    </div>

                    {/* ── SECTION: Take Rate ── */}
                    {data.grossVolume > 0 && (
                        <div className="kpi-section">
                            <h2 className="section-title">Take Rate</h2>
                            <div className="revenue-take-rate-box">
                                {/* Take Rate Stimato — primary */}
                                <div className="revenue-take-rate-primary">
                                    <div className="revenue-take-rate-label">Take Rate Stimato</div>
                                    <div className="revenue-take-rate-value" style={{ color: '#6366f1' }}>
                                        {estimatedTakeRate != null ? formatPct(estimatedTakeRate * 100) : '—'}
                                    </div>
                                    <div className="revenue-take-rate-formula">
                                        Fee stimata ÷ GTV coperto da snapshot
                                    </div>
                                    {data.feeDataCoverage < 100 && (
                                        <div className="revenue-take-rate-caveat">
                                            ⚠ Stima: copertura snapshot {data.feeDataCoverage}%. Il take rate reale potrebbe differire.
                                        </div>
                                    )}
                                </div>

                                {/* Rapporto fatture/GTV — secondary, administrative only */}
                                {data.confirmedRevenue?.amount > 0 && (
                                    <div className="revenue-take-rate-secondary">
                                        <div className="revenue-take-rate-label revenue-take-rate-label--muted">
                                            Rapporto fatture emesse / GTV
                                            <span style={{ marginLeft: '5px', cursor: 'help' }} title="Indicatore amministrativo. NON equivale al take rate commerciale: le fatture sono filtrate per data emissione, il GTV per data transazione. Le basi dati non sono allineate senza riconciliazione esplicita.">
                                                <Info size={12} color="#94a3b8" />
                                            </span>
                                        </div>
                                        <div className="revenue-take-rate-value revenue-take-rate-value--sm">
                                            {invoiceToGtvRatio != null ? formatPct(invoiceToGtvRatio * 100) : '—'}
                                        </div>
                                        <div className="revenue-take-rate-formula">
                                            Fatture emesse ÷ GTV periodo
                                        </div>
                                        <div className="revenue-take-rate-caveat">
                                            Indicatore amministrativo — non take rate commerciale salvo riconciliazione esplicita.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── SECTION: Revenue by Venue ── */}
                    <div className="kpi-section">
                        <h2 className="section-title">Revenue per Venue</h2>
                        {(data.revenueByVenue || []).length === 0 ? (
                            <EmptyStateGuide
                                message="Nessun dato di revenue per venue nel periodo selezionato."
                                hint="Seleziona un periodo con transazioni registrate."
                            />
                        ) : (
                            <>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="analytics-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Venue</th>
                                                <th>Tenant</th>
                                                <th style={{ textAlign: 'right' }}>GTV</th>
                                                <th style={{ textAlign: 'right' }}>Fee Stimata</th>
                                                <th style={{ textAlign: 'right' }}>Take Rate</th>
                                                <th style={{ textAlign: 'right' }}>Transazioni</th>
                                                <th style={{ textAlign: 'right' }}>Snapshot</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleVenues.map((v, idx) => {
                                                const venueTakeRate = v.gtv > 0 ? (v.estimatedFee / v.gtv) * 100 : null;
                                                const venueCoverage = v.transactionCount > 0
                                                    ? Math.round((v.coverageCount / v.transactionCount) * 100)
                                                    : 0;
                                                return (
                                                    <tr key={v.venueId}>
                                                        <td style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{idx + 1}</td>
                                                        <td style={{ fontWeight: 500 }}>{v.venueName}</td>
                                                        <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{v.tenantName}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatCurrency(v.gtv)}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            {formatCurrency(v.estimatedFee)}
                                                            {v.coverageCount < v.transactionCount && (
                                                                <span style={{ marginLeft: '4px', fontSize: '0.7rem', color: '#f59e0b' }} title="Fee stimata parziale: alcune transazioni senza snapshot">⚠</span>
                                                            )}
                                                        </td>
                                                        <td style={{ textAlign: 'right', color: '#6366f1' }}>
                                                            {venueTakeRate != null ? formatPct(venueTakeRate) : '—'}
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>{formatNumber(v.transactionCount)}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <span style={{
                                                                color: venueCoverage === 100 ? '#10b981' : venueCoverage > 0 ? '#f59e0b' : '#ef4444',
                                                                fontWeight: 600, fontSize: '0.85rem'
                                                            }}>
                                                                {venueCoverage}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {(data.revenueByVenue || []).length > VENUE_TABLE_LIMIT && (
                                    <button
                                        className="revenue-show-more-btn"
                                        onClick={() => setShowAllVenues(v => !v)}
                                    >
                                        {showAllVenues
                                            ? <><ChevronUp size={14} /> Mostra meno</>
                                            : <><ChevronDown size={14} /> Vedi tutte le {data.revenueByVenue.length} venue</>}
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* ── SECTION: Fatture emesse — dettaglio riconciliazione ── */}
                    <div className="kpi-section">
                        <h2 className="section-title">Fatture emesse — dettaglio</h2>
                        <div className="revenue-invoice-caveat">
                            <Info size={13} style={{ flexShrink: 0, marginTop: '2px' }} />
                            <span>
                                Periodo competenza non disponibile: le fatture sono mostrate per data emissione.
                                Per verificare se una fattura si riferisce alle transazioni del periodo selezionato è necessaria una riconciliazione esplicita.
                            </span>
                        </div>
                        {(data.invoiceList || []).length === 0 ? (
                            <EmptyStateGuide
                                message="Nessuna fattura emessa nel periodo selezionato."
                                hint="Le fatture sono filtrate per data emissione (invoiceDate)."
                            />
                        ) : (
                            <>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="analytics-table">
                                        <thead>
                                            <tr>
                                                <th>N° Fattura</th>
                                                <th>Tenant</th>
                                                <th>Venue</th>
                                                <th>Data emissione</th>
                                                <th style={{ textAlign: 'right' }}>Importo</th>
                                                <th>Stato</th>
                                                <th>Note</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleInvoices.map(inv => (
                                                <tr key={inv.id}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{inv.invoiceNumber}</td>
                                                    <td style={{ fontSize: '0.85rem' }}>{inv.tenantName}</td>
                                                    <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{inv.venueName || '—'}</td>
                                                    <td style={{ fontSize: '0.85rem' }}>{formatDate(inv.invoiceDate)}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(inv.amountGross)}</td>
                                                    <td>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            padding: '2px 8px',
                                                            borderRadius: '99px',
                                                            fontSize: '0.72rem',
                                                            fontWeight: 600,
                                                            background: INVOICE_STATUS_COLOR[inv.status] + '22',
                                                            color: INVOICE_STATUS_COLOR[inv.status],
                                                        }}>
                                                            {INVOICE_STATUS_LABEL[inv.status] || inv.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '0.75rem', color: '#94a3b8', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {inv.description || '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {(data.invoiceList || []).length > 10 && (
                                    <button
                                        className="revenue-show-more-btn"
                                        onClick={() => setShowAllInvoices(v => !v)}
                                    >
                                        {showAllInvoices
                                            ? <><ChevronUp size={14} /> Mostra meno</>
                                            : <><ChevronDown size={14} /> Vedi tutte le {data.invoiceList.length} fatture</>}
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* ── SECTION: Scomposizione mance e fee ── */}
                    {data.transferBreakdown && (
                        <div className="kpi-section">
                            <h2 className="section-title">Scomposizione mance e fee</h2>

                            {/* Fixed caveat banner */}
                            <div className="revenue-invoice-caveat" style={{ marginBottom: '12px' }}>
                                <Info size={13} style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span>{data.transferBreakdown.caveats[0]}</span>
                            </div>

                            {/* Partial-coverage warning */}
                            {data.transferBreakdown.transferFeeCoveragePercent < 100 && data.transferBreakdown.transferCount > 0 && (
                                <div className="revenue-anomaly-warning" style={{ marginBottom: '12px' }}>
                                    <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
                                    <div>
                                        <strong>Dato parziale:</strong> la commissione Stripe non è valorizzata su tutti i transfer
                                        ({data.transferBreakdown.transferWithStripeFeeCount} su {data.transferBreakdown.transferCount} transfer).
                                    </div>
                                </div>
                            )}

                            {data.transferBreakdown.transferCount === 0 ? (
                                <div className="analytics-empty-state">
                                    <p>Nessun transfer trovato nel periodo selezionato.</p>
                                </div>
                            ) : (
                                <div className="kpi-grid">
                                    {/* Fee Soontip trattenuta */}
                                    <div className="kpi-card kpi-primary">
                                        <div className="kpi-icon"><span style={{ fontSize: '20px', fontWeight: 700 }}>%</span></div>
                                        <div className="kpi-content">
                                            <div className="kpi-label">
                                                Fee Soontip trattenuta
                                                <span style={{ marginLeft: '6px', cursor: 'help' }} title="Formula: SUM(TipTransfer.feeSoontip). Fee Soontip registrata sui transfer del periodo, al netto dei transfer annullati (reversed = false). Rappresenta il ricavo Soontip sui transfer. Unità: euro.">
                                                    <Info size={12} color="#94a3b8" />
                                                </span>
                                            </div>
                                            <div className="kpi-value">{formatCurrency(data.transferBreakdown.transferFeeSoontipTotal)}</div>
                                            <div className="kpi-meta"><span>Ricavo Soontip sui transfer del periodo</span></div>
                                        </div>
                                    </div>

                                    {/* Commissione Stripe dedotta dalle mance */}
                                    <div className="kpi-card kpi-info">
                                        <div className="kpi-icon"><span style={{ fontSize: '20px' }}>⚡</span></div>
                                        <div className="kpi-content">
                                            <div className="kpi-label">
                                                Commissione Stripe dedotta dalle mance
                                                <span style={{ marginLeft: '6px', cursor: 'help' }} title="Formula: SUM(TipTransfer.feeStripe). Commissione Stripe rilevata al momento del pagamento e dedotta dalla mancia prima della distribuzione ai percettori. Non è un costo Soontip: viene sottratta dalla mancia lorda insieme alla fee Soontip prima dello split.">
                                                    <Info size={12} color="#94a3b8" />
                                                </span>
                                            </div>
                                            <div className="kpi-value">{formatCurrency(data.transferBreakdown.transferStripeFeeTotal)}</div>
                                            <div className="kpi-meta"><span>Commissione Stripe nota al momento del pagamento</span></div>
                                        </div>
                                    </div>

                                    {/* Mance nette assegnate */}
                                    <div className="kpi-card kpi-secondary">
                                        <div className="kpi-icon"><TrendingUp size={28} /></div>
                                        <div className="kpi-content">
                                            <div className="kpi-label">
                                                Mance nette assegnate
                                                <span style={{ marginLeft: '6px', cursor: 'help' }} title="Formula: SUM(TipTransfer.amountNet). Importo residuo assegnato ai percettori dopo deduzione della commissione Stripe e della fee Soontip. Corrisponde a: mancia lorda − commissione Stripe − fee Soontip.">
                                                    <Info size={12} color="#94a3b8" />
                                                </span>
                                            </div>
                                            <div className="kpi-value">{formatCurrency(data.transferBreakdown.transferAmountNetTotal)}</div>
                                            <div className="kpi-meta"><span>Importo residuo dopo commissione Stripe e fee Soontip</span></div>
                                        </div>
                                    </div>

                                    {/* Copertura commissione Stripe */}
                                    <div className="kpi-card kpi-info">
                                        <div className="kpi-icon"><span style={{ fontSize: '20px' }}>🔍</span></div>
                                        <div className="kpi-content">
                                            <div className="kpi-label">
                                                Copertura commissione Stripe
                                                <span style={{ marginLeft: '6px', cursor: 'help' }} title="Formula: transfer con feeStripe valorizzata / transfer totali. Metrica di qualità dato: indica la percentuale di transfer per cui la commissione Stripe è disponibile. Se < 100%, la commissione Stripe sui transfer restanti non è disponibile nel DB.">
                                                    <Info size={12} color="#94a3b8" />
                                                </span>
                                            </div>
                                            <div className="kpi-value" style={{
                                                color: data.transferBreakdown.transferFeeCoveragePercent === 100
                                                    ? '#10b981'
                                                    : data.transferBreakdown.transferFeeCoveragePercent > 0
                                                        ? '#f59e0b'
                                                        : '#ef4444'
                                            }}>
                                                {formatPct(data.transferBreakdown.transferFeeCoveragePercent)}
                                            </div>
                                            <div className="kpi-meta">
                                                <span>Transfer con commissione Stripe valorizzata</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Costi operativi non tracciati */}
                                    <div className="kpi-card" style={{ borderLeft: '4px solid #94a3b8', opacity: 0.85 }}>
                                        <div className="kpi-icon"><span style={{ fontSize: '20px' }}>📋</span></div>
                                        <div className="kpi-content">
                                            <div className="kpi-label">
                                                Costi operativi Soontip non tracciati
                                                <span style={{ marginLeft: '6px', cursor: 'help' }} title="Costi non presenti nel DB e non inclusi in questa vista: A-Cube (fatturazione elettronica), infrastruttura, dispute/chargeback, commissioni Stripe Connect, commissioni payout e altri costi operativi aziendali.">
                                                    <Info size={12} color="#94a3b8" />
                                                </span>
                                            </div>
                                            <div className="kpi-value" style={{ color: '#94a3b8', fontStyle: 'italic' }}>TBD</div>
                                            <div className="kpi-meta"><span>A-Cube, infrastruttura, dispute, Connect/payout e altri costi non inclusi</span></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </AnalyticsLayout>
    );
}
